/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { CircularProgress, Grid, IconButton, Typography } from '@material-ui/core';
import { Close as DeleteIcon, ErrorOutline, Done } from '@material-ui/icons';
import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import {
  hasType,
  getDocumentType,
  getFigureToBePublished,
  getNumberOfClaims,
  getNumberOfDrawings,
  specialDocumentTypes,
  specialDocumentsInAnotherLanguage,
} from '../../services/constants';
import Tooltip from '../../../../common/Tooltip';
import { humanFileSize, toEllipsis } from '../../../../../shared/utils/stringUtils';

const useStyles = makeStyles(theme => ({
  gridContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    '& > button': {
      backgroundColor: 'transparent',
    },
  },
  fileInformation: {
    justifyContent: 'left',
    flexDirection: 'row',
  },
  filenameText: {
    color: theme.palette.titleText,
    marginLeft: 0,
  },
  byteSize: {
    justifyContent: 'right',
    flexDirection: 'row',
  },
  deleteIconGrid: {
    justifyContent: 'right',
    flexDirection: 'row',
  },
  deleteIcon: {
    color: theme.palette.titleText,
  },
  lightText: {
    color: theme.palette.titleText,
    marginLeft: '0.3rem',
  },
  strongText: {
    marginLeft: '0.3rem',
    color: theme.palette.titleText,
    fontWeight: 600,
    maxWidth: '10rem',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  lineText: {
    display: 'flex',
  },
}));

/**
 * Component that lists an uploaded item within the Dropzone component.
 * @param originalFileName
 * @param item
 * @param onDeletePreviewFile
 * @param readOnly
 * @returns {JSX.Element}
 * @constructor
 */
export function AttachmentPreviewItem({ originalFileName, item, onDeletePreviewFile, readOnly }) {
  const { t } = useTranslation();
  const classes = useStyles();

  return (
    <Grid
      container
      spacing={1}
      key={originalFileName}
      id={`${item.attachmentType}filePreviewRow`}
      name="filePreviewRow"
    >
      <Grid classes={{ root: classes.gridContainer }} name="uploadProgress" item xs={1} md={1}>
        {item.error && (
          <IconButton aria-label="Failed">
            <ErrorOutline color="default" />
          </IconButton>
        )}
        {item.progress && !item.error && (
          <CircularProgress aria-label="Progress" variant="static" value={item.progress} />
        )}
        {!item.error && !item.progress && (
          <IconButton aria-label="Success">
            <Done color="primary" />
          </IconButton>
        )}
      </Grid>
      <Grid
        classes={{ root: classes.gridContainer }}
        className={classes.fileInformation}
        name="fileInformation"
        item
        xs={9}
        md={9}
      >
        <Typography variant="body1" component="div" className={classes.lineText}>
          <Tooltip title={item.name} placement="top" arrow>
            <Typography variant="body1" component="div" className={classes.filenameText}>
              {toEllipsis(item.name, 40)}
            </Typography>
          </Tooltip>

          {(hasType(item, specialDocumentTypes.CLAIMS) ||
            hasType(item, specialDocumentsInAnotherLanguage.CLAIMS_IN_ANOTHER_LANGUAGE)) &&
            getNumberOfClaims(item) > 0 && (
            <>
              <Typography variant="body1" component="div" className={classes.lightText}>
                |
              </Typography>
              <Typography variant="body1" component="div" className={classes.lightText}>
                {t('claims.number.colon')}
              </Typography>
              <Typography variant="body1" component="div" className={classes.strongText}>
                {item.numberOfClaims}
              </Typography>
            </>
          )}

          {(hasType(item, specialDocumentTypes.ABSTRACT) ||
            hasType(item, specialDocumentsInAnotherLanguage.ABSTRACT_IN_ANOTHER_LANGUAGE)) &&
            getFigureToBePublished(item) && (
            <>
              <Typography variant="body1" component="div" className={classes.lightText}>
                |
              </Typography>
              <Typography variant="body1" component="div" className={classes.lightText}>
                {t('attachment.figure.published.colon')}
              </Typography>
              <Typography variant="body1" component="div" className={classes.strongText}>
                {item.figureToBePublished}
              </Typography>
            </>
          )}

          {(hasType(item, specialDocumentTypes.DRAWINGS) ||
            hasType(item, specialDocumentsInAnotherLanguage.DRAWINGS_IN_ANOTHER_LANGUAGE)) &&
            getNumberOfDrawings(item) > 0 && (
            <>
              <Typography variant="body1" component="div" className={classes.lightText}>
                |
              </Typography>
              <Typography variant="body1" component="div" className={classes.lightText}>
                {t('drawings.number.colon')}
              </Typography>
              <Typography variant="body1" component="div" className={classes.strongText}>
                {item.numberOfDrawings}
              </Typography>
            </>
          )}

          {hasType(item, specialDocumentTypes.OTHER) && getDocumentType(item) && (
            <>
              <Typography variant="body1" component="div" className={classes.lightText}>
                |
              </Typography>
              <Typography variant="body1" component="div" className={classes.lightText}>
                {t('attachments.type.document.colon')}
              </Typography>
              <Typography variant="body1" component="div" className={classes.strongText}>
                {item.documentType}
              </Typography>
            </>
          )}
        </Typography>
      </Grid>
      <Grid classes={{ root: classes.gridContainer }} className={classes.byteSize} item xs={1} md={1}>
        <Typography variant="body1">{humanFileSize(item.byteSize)}</Typography>
      </Grid>
      <Grid classes={{ root: classes.gridContainer }} className={classes.deleteIconGrid} item xs={1} md={1}>
        {!readOnly && (
          <IconButton
            id={`${item.name}-delete`}
            aria-label="Delete"
            onClick={() => onDeletePreviewFile(originalFileName)}
            className={classes.deleteIcon}
          >
            <DeleteIcon fontSize="default" />
          </IconButton>
        )}
      </Grid>
    </Grid>
  );
}
